/**
 * ============================================================================
 * WHICH THREAD THE READER IS ACTUALLY IN
 * ============================================================================
 *
 * A comment thread polls at 1 Hz. That was affordable when exactly one thread
 * existed — the post page's — and is not affordable now that a feed card reads
 * the same query: twenty cards on a page would be twenty requests a second for
 * conversations nobody is having.
 *
 * WHAT CHANGED THE BAR. Since OSO-563 a write is spliced into the thread cache
 * by `useCreateComment`, so polling is no longer how you see YOUR OWN comment
 * — that is instant and does not depend on a cadence, an `enabled` flag or a
 * scroll position. Polling now decides one thing only: how fast you see
 * SOMEBODY ELSE'S. So it is worth a request a second where a second of latency
 * matters, and worth nothing anywhere else.
 *
 * THE PREDICATE IS INTENT, NOT PROXIMITY. Being on screen is not being in a
 * conversation — a reader scrolling past three strangers' posts would open
 * three polls. What counts is doing something only a participant does: opening
 * a reply box, expanding a branch, writing, or reacting to a comment. See
 * `enterThread`'s callers for the complete list.
 *
 * THREE CONSTANTS, AND WHY EACH IS THE NUMBER IT IS:
 *
 *  - ONE live thread per tab. It bounds the whole tab at one request a second
 *    however long the feed is. Following two conversations at once is rare
 *    enough that the second one can wait for the feed's own tick; if that
 *    turns out to be a real complaint, this is the line to change.
 *  - THREE MINUTES. It has to outlast composing a reply, which is the long
 *    pause. Every counted interaction restarts it, including typing.
 *  - LEAVING IS LAZY. Closing the composer or collapsing a branch does not end
 *    it; only the timer does. Otherwise the state would flap while somebody is
 *    still clicking around in the same conversation.
 *
 * A module-level store rather than context: it is per tab, every reader of it
 * is a leaf, and threading a provider through the feed would be ceremony for
 * one number. `useSyncExternalStore` is how components read it.
 */

const LIVE_FOR_MS = 3 * 60 * 1000;

let liveThread: number | null = null;
/**
 * WHERE THE INTENT WAS EXPRESSED.
 *
 * Without it the window survives navigation, and an intent expressed on a page
 * the reader has left satisfies a predicate that is supposed to mean "in this
 * conversation": reply to post P in the home feed, move to a group page inside
 * the three minutes, and a card for P there would poll at 1 Hz having been
 * touched by nobody. `useThreadFocus` compares it against the current route.
 */
let livePath: string | null = null;
let expiry: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function publish(next: number | null, path: string | null): void {
  if (liveThread === next && livePath === path) return;
  liveThread = next;
  livePath = path;
  for (const listener of listeners) listener();
}

/**
 * The reader did something only a participant does. Idempotent: calling it
 * again for the thread already live just restarts the window.
 */
export function enterThread(postId: number): void {
  if (expiry) clearTimeout(expiry);
  expiry = setTimeout(() => {
    expiry = null;
    publish(null, null);
  }, LIVE_FOR_MS);
  publish(postId, typeof window === "undefined" ? null : window.location.pathname);
}

export function subscribeToThreadFocus(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function liveThreadId(): number | null {
  return liveThread;
}

export function liveThreadPath(): string | null {
  return livePath;
}

/** The server never renders a live thread, so the server snapshot is "none". */
export function serverLiveThreadId(): null {
  return null;
}

/**
 * Test seam — and safe to call at any point, which is why it does NOT drop the
 * subscribers. React holds an unsubscribe for a listener it registered; taking
 * that listener out of the set behind its back leaves the component
 * permanently deaf to every later focus change.
 */
export function __resetThreadFocus(): void {
  if (expiry) clearTimeout(expiry);
  expiry = null;
  publish(null, null);
}
